// Composable for Postcard API usage
import type {
  ApiIssueCollection,
  ApiResult,
  CampaignStatsResponse,
  CreatePostcardRequest,
  PostcardResponse,
  PostcardPreviewResponse,
  SenderAddress,
  RecipientAddress,
} from '../types'

interface UsePostcardApiOptions {
  onError?: (error: Error) => void
  onSuccess?: (message: string) => void
}

export function usePostcardApi(options: UsePostcardApiOptions = {}) {
  const loading = ref(false)
  const error = ref<string | null>(null)

  const { onError, onSuccess } = options

  const clearError = () => {
    error.value = null
  }

  // Helper to extract and format errors/warnings from API response
  function getApiMessages(
    result: ApiIssueCollection | null | undefined,
  ): string | null {
    const errors = Array.isArray(result?.errors) ? result.errors : []
    const warnings = Array.isArray(result?.warnings) ? result.warnings : []
    const errorMsgs = errors.map((issue) => {
      const message = issue.description ?? issue.message ?? 'Unknown error'
      return `[${issue.code}] ${message}`
    })
    const warningMsgs = warnings.map((issue) => {
      const message = issue.description ?? issue.message ?? 'Unknown warning'
      return `[${issue.code}] ${message}`
    })
    if (errorMsgs.length || warningMsgs.length) {
      return [...errorMsgs, ...warningMsgs].join(' | ')
    }
    return null
  }

  const handleError = (err: unknown) => {
    const errorMessage = err instanceof Error ? err.message : 'Unknown error'
    error.value = errorMessage
    onError?.(err instanceof Error ? err : new Error(errorMessage))
  }

  const handleSuccess = (message: string) => {
    onSuccess?.(message)
  }

  const getCampaignStats = async (
    campaignKey: string,
  ): Promise<CampaignStatsResponse | null> => {
    if (!campaignKey) {
      handleError(new Error('campaignKey is required'))
      return null
    }
    loading.value = true
    clearError()
    try {
      const result = await $fetch<ApiResult<CampaignStatsResponse>>(
        `/api/postcard/campaigns/${encodeURIComponent(campaignKey)}`,
      )
      const issues = getApiMessages(result)
      if (issues) {
        error.value = issues
        onError?.(new Error(issues))
        return null
      }
      handleSuccess('Campaign stats fetched successfully')
      return result
    } catch (err) {
      handleError(err)
      return null
    } finally {
      loading.value = false
    }
  }

  const createPostcard = async (
    campaignKey: string,
    data: CreatePostcardRequest,
  ): Promise<PostcardResponse | null> => {
    loading.value = true
    clearError()
    try {
      if (!campaignKey) {
        throw new Error('campaignKey is required')
      }
      const result = await $fetch<ApiResult<PostcardResponse>>(
        `/api/postcard/postcards?campaignKey=${encodeURIComponent(campaignKey)}`,
        {
          method: 'POST',
          body: data,
        },
      )
      const issues = getApiMessages(result)
      if (issues) {
        error.value = issues
        onError?.(new Error(issues))
        return null
      }
      handleSuccess('Postcard created successfully')
      return result
    } catch (err) {
      handleError(err)
      return null
    } finally {
      loading.value = false
    }
  }

  const getPostcard = async (
    cardKey: string,
  ): Promise<PostcardResponse | null> => {
    loading.value = true
    clearError()
    try {
      const result = await $fetch<ApiResult<PostcardResponse>>(
        `/api/postcard/postcards/${cardKey}`,
      )
      const issues = getApiMessages(result)
      if (issues) {
        error.value = issues
        onError?.(new Error(issues))
        return null
      }
      return result
    } catch (err) {
      handleError(err)
      return null
    } finally {
      loading.value = false
    }
  }

  const uploadImage = async (
    cardKey: string,
    image: File,
  ): Promise<boolean> => {
    loading.value = true
    clearError()
    try {
      const formData = new FormData()
      formData.append('image', image)

      const result = await $fetch<ApiIssueCollection>(
        `/api/postcard/postcards/${cardKey}/image`,
        {
          method: 'PUT',
          body: formData,
        },
      )
      const issues = getApiMessages(result)
      if (issues) {
        error.value = issues
        onError?.(new Error(issues))
        return false
      }
      handleSuccess('Image uploaded successfully')
      return true
    } catch (err) {
      handleError(err)
      return false
    } finally {
      loading.value = false
    }
  }

  const setStampImage = async (
    cardKey: string,
    stamp: File,
  ): Promise<boolean> => {
    loading.value = true
    clearError()
    try {
      const formData = new FormData()
      formData.append('image', stamp)

      const result = await $fetch<ApiIssueCollection>(
        `/api/postcard/postcards/${cardKey}/branding/stamp`,
        {
          method: 'PUT',
          body: formData,
        },
      )
      const issues = getApiMessages(result)
      if (issues) {
        error.value = issues
        onError?.(new Error(issues))
        return false
      }
      handleSuccess('Stamp image uploaded successfully')
      return true
    } catch (err) {
      handleError(err)
      return false
    } finally {
      loading.value = false
    }
  }

  const getPostcardPreviewFront = async (
    cardKey: string,
  ): Promise<PostcardPreviewResponse | null> => {
    loading.value = true
    clearError()
    try {
      const result = await $fetch<ApiResult<PostcardPreviewResponse>>(
        `/api/postcard/postcards/${cardKey}/previews/front`,
      )
      const issues = getApiMessages(result)
      if (issues) {
        error.value = issues
        onError?.(new Error(issues))
        return null
      }
      return result
    } catch (err) {
      handleError(err)
      return null
    } finally {
      loading.value = false
    }
  }

  const getPostcardPreviewBack = async (
    cardKey: string,
  ): Promise<PostcardPreviewResponse | null> => {
    loading.value = true
    clearError()
    try {
      const result = await $fetch<ApiResult<PostcardPreviewResponse>>(
        `/api/postcard/postcards/${cardKey}/previews/back`,
      )
      const issues = getApiMessages(result)
      if (issues) {
        error.value = issues
        onError?.(new Error(issues))
        return null
      }
      return result
    } catch (err) {
      handleError(err)
      return null
    } finally {
      loading.value = false
    }
  }

  const updateSenderAddress = async (
    cardKey: string,
    senderAddress: SenderAddress,
  ): Promise<boolean> => {
    loading.value = true
    clearError()
    try {
      const result = await $fetch<ApiIssueCollection>(
        `/api/postcard/postcards/${cardKey}/addresses/sender`,
        {
          method: 'PUT',
          body: senderAddress,
        },
      )
      const issues = getApiMessages(result)
      if (issues) {
        error.value = issues
        onError?.(new Error(issues))
        return false
      }
      handleSuccess('Sender address updated successfully')
      return true
    } catch (err) {
      handleError(err)
      return false
    } finally {
      loading.value = false
    }
  }

  const updateRecipientAddress = async (
    cardKey: string,
    recipientAddress: RecipientAddress,
  ): Promise<boolean> => {
    loading.value = true
    clearError()
    try {
      const result = await $fetch<ApiIssueCollection>(
        `/api/postcard/postcards/${cardKey}/addresses/recipient`,
        {
          method: 'PUT',
          body: recipientAddress,
        },
      )
      const issues = getApiMessages(result)
      if (issues) {
        error.value = issues
        onError?.(new Error(issues))
        return false
      }
      handleSuccess('Recipient address updated successfully')
      return true
    } catch (err) {
      handleError(err)
      return false
    } finally {
      loading.value = false
    }
  }

  const setSenderText = async (
    cardKey: string,
    senderText: string,
  ): Promise<boolean> => {
    loading.value = true
    clearError()
    try {
      const result = await $fetch<ApiIssueCollection>(
        `/api/postcard/postcards/${cardKey}/sendertext`,
        {
          method: 'PUT',
          body: { senderText },
        },
      )
      const issues = getApiMessages(result)
      if (issues) {
        error.value = issues
        onError?.(new Error(issues))
        return false
      }
      handleSuccess('Sender text updated successfully')
      return true
    } catch (err) {
      handleError(err)
      return false
    } finally {
      loading.value = false
    }
  }

  const approvePostcard = async (cardKey: string): Promise<boolean> => {
    loading.value = true
    clearError()
    try {
      const result = await $fetch<ApiIssueCollection>(
        `/api/postcard/postcards/${cardKey}/approval`,
        {
          method: 'POST',
        },
      )
      const issues = getApiMessages(result)
      if (issues) {
        error.value = issues
        onError?.(new Error(issues))
        return false
      }
      handleSuccess('Postcard approval updated successfully')
      return true
    } catch (err) {
      handleError(err)
      return false
    } finally {
      loading.value = false
    }
  }

  return {
    loading: readonly(loading),
    error: readonly(error),
    clearError,
    getCampaignStats,
    createPostcard,
    getPostcard,
    uploadImage,
    getPostcardPreviewFront,
    getPostcardPreviewBack,
    updateSenderAddress,
    updateRecipientAddress,
    setStampImage,
    setSenderText,
    approvePostcard,
  }
}
